import { useState, useEffect, useCallback } from "react";

type ClipboardRegionType = "single" | "multi";

/*
 * Custom hook to check whether the clipboard contains a valid region payload of the given type.
 *
 * Returns:
 *   - canPaste: boolean — true when the clipboard holds a valid payload matching the requested type
 *   - checkClipboard: () => Promise<void> — call this to re-validate (e.g. on hover/focus)
 *
 * Usage:
 *   const { canPaste, checkClipboard } = useClipboardRegionCheck("multi");
 *   const { canPaste, checkClipboard } = useClipboardRegionCheck("single");
 */
export const useClipboardRegionCheck = (type: ClipboardRegionType) => {
  /* State */
  const [canPaste, setCanPaste] = useState(false);

  /* Handlers */
  const checkClipboard = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      const payload = JSON.parse(text);

      // Validate the payload structure based on the expected type
      if (type === "multi") {
        setCanPaste(payload.type === "multi" && Array.isArray(payload.regions) && payload.regions.length > 0);
      } else {
        setCanPaste(payload.type === "single" && payload.region != null);
      }
    } catch {
      setCanPaste(false);
    }
  }, [type]);

  /* Side Effects */
  useEffect(() => {
    checkClipboard();
  }, [checkClipboard]);

  return { canPaste, checkClipboard };
};
